import { useCallback, useState } from "react";
import { useToast } from "@/app/providers/ToastProvider";
import {
  listDownloading,
  removeDownload,
  startDownload,
  stopDownload,
  type DownloaderTorrent,
} from "@/features/download/api";
import { usePolling } from "@/shared/hooks/usePolling";
import { formatBytes } from "@/shared/lib/format";
import { Button } from "@/shared/ui/Button";
import { EmptyState } from "@/shared/ui/EmptyState";
import { PageHeader } from "@/shared/ui/PageHeader";
import { Spinner } from "@/shared/ui/Spinner";

const HASH_RE = /^[a-fA-F0-9]{16,64}$/;

function isSafeHash(hash?: string | null): hash is string {
  return Boolean(hash && HASH_RE.test(hash));
}

/** 下载看板：轮询 + 启停删 */
export function DownloadPage() {
  const { toast } = useToast();
  const [busyHash, setBusyHash] = useState<string | null>(null);

  const fetcher = useCallback(() => listDownloading(), []);
  const { data, error, loading, refresh } = usePolling<DownloaderTorrent[]>({
    fetcher,
    intervalMs: 4000,
  });

  const items = data ?? [];

  async function act(hash: string, fn: () => Promise<unknown>, ok: string) {
    if (!isSafeHash(hash)) {
      toast("无效的任务标识", "error");
      return;
    }
    setBusyHash(hash);
    try {
      await fn();
      toast(ok, "success");
      await refresh(true);
    } catch (e) {
      toast(e instanceof Error ? e.message : "操作失败", "error");
    } finally {
      setBusyHash(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="下载"
        description={`${items.length} 个任务 · 每 4 秒刷新`}
        actions={
          <Button variant="ghost" onClick={() => void refresh()} disabled={loading}>
            刷新
          </Button>
        }
      />

      {loading && !data ? (
        <div className="flex items-center gap-2 text-[13px] text-fg-muted">
          <Spinner /> 加载中…
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 rounded-[var(--radius-md)] border border-danger/30 bg-danger/10 px-4 py-3 text-[13px] text-danger">
          {error}
        </div>
      ) : null}

      {!loading && !error && items.length === 0 ? (
        <EmptyState title="当前没有下载任务" description="从搜索页添加种子" />
      ) : null}

      <ul className="flex flex-col gap-2">
        {items.map((t) => {
          const hash = t.hash || "";
          const busy = busyHash === hash;
          const progress = Math.min(Math.max(t.progress ?? 0, 0), 100);
          const paused =
            t.state === "pausedDL" ||
            t.state === "pausedUP" ||
            t.state === "paused";
          return (
            <li
              key={hash || t.title}
              className="rounded-[var(--radius-md)] border border-border bg-bg-card p-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-[14px] font-medium text-fg">
                    {t.title || t.name || "未命名任务"}
                  </h3>
                  <p className="mt-0.5 text-[12px] text-fg-subtle">
                    {[
                      t.season_episode,
                      formatBytes(t.size),
                      t.state,
                      t.dlspeed ? `↓ ${t.dlspeed}` : null,
                      t.left_time ? `剩余 ${t.left_time}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                </div>
                <div className="flex gap-2">
                  {paused ? (
                    <Button
                      variant="ghost"
                      disabled={busy || !isSafeHash(hash)}
                      onClick={() =>
                        void act(hash, () => startDownload(hash), "已开始")
                      }
                    >
                      开始
                    </Button>
                  ) : (
                    <Button
                      variant="ghost"
                      disabled={busy || !isSafeHash(hash)}
                      onClick={() =>
                        void act(hash, () => stopDownload(hash), "已暂停")
                      }
                    >
                      暂停
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    disabled={busy || !isSafeHash(hash)}
                    onClick={() => {
                      if (!window.confirm("确定删除该下载任务？")) return;
                      void act(hash, () => removeDownload(hash), "已删除");
                    }}
                  >
                    删除
                  </Button>
                </div>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-bg-hover">
                <div
                  className="h-full rounded-full bg-brand transition-[width] duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="mt-1 text-right text-[11px] text-fg-subtle">
                {progress.toFixed(1)}%
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
